/**
 * Client-side session state.
 *
 * Asks the API "who am I?" — the session cookie is httpOnly, so the browser
 * can't inspect it and auth state is whatever the API reports. This is a UX
 * guard only; the API independently enforces the whitelist on every call.
 */
import { useEffect, useState } from "react";
import { api, type Me } from "../lib/api";

export type { Me };

export type SessionState =
  | { status: "loading" }
  | { status: "authenticated"; me: Me }
  | { status: "anonymous" };

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    let active = true;

    function check() {
      if (active) setState({ status: "loading" });
      api
        .me()
        .then((me) => {
          if (active) setState({ status: "authenticated", me });
        })
        .catch(() => {
          // 401/403 → signed out. Network errors land here too; treating them as
          // anonymous is deliberate for now (TODO: distinguish so a blip doesn't
          // look like a logout).
          if (active) setState({ status: "anonymous" });
        });
    }

    check();

    // Logging out is a full-page navigation away from the SPA (a form POST), so
    // the page we're leaving is eligible for the browser's back/forward cache:
    // pressing Back can *restore* that exact JS state (still "authenticated",
    // already-mounted app) instead of reloading it, which would otherwise leave
    // the logged-out user staring at a stale authed page. `pageshow` with
    // `persisted: true` is the one event guaranteed to fire on such a restore —
    // re-check the session then so a cleared cookie bounces back to /login.
    function onPageShow(e: PageTransitionEvent) {
      if (e.persisted) check();
    }
    window.addEventListener("pageshow", onPageShow);

    return () => {
      active = false;
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  return state;
}
